import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Tabs, Tab, Button, Stack, useTheme, alpha, useMediaQuery } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AppsIcon from '@mui/icons-material/Apps';
import StorageIcon from '@mui/icons-material/Storage';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AddIcon from '@mui/icons-material/Add';
import { useElementSize } from '@custom-react-hooks/use-element-size';

import MyUserAppInstances from '@/pages/instances/userAppInstancesPage';
import MyDataSourceInstances from '@/pages/instances/dataSourceInstancesPage';
import MyAgentInstances from '@/pages/instances/agentInstancesPage';
import TabPanel from '@/components/tabPanel';

const InstancesPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [containerRef, containerSize] = useElementSize();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const tabParam = searchParams.get('tab') || 'userAppInstances';

  const [selectedTab, setSelectedTab] = useState<string>(tabParam);

  useEffect(() => {
    if (tabParam !== selectedTab) {
      setSelectedTab(tabParam);
    }
  }, [tabParam]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSearchParams({ tab: newValue });
    setSelectedTab(newValue);
  };

  const getAddRoute = () => {
    switch (selectedTab) {
      case 'userAppInstances':
        return '/createUserAppInstance';
      case 'dataSourceInstances':
        return '/createDataSourceInstance';
      case 'agentInstances':
        return '/createAgentInstance';
      default:
        return '/';
    }
  };

  const getAddLabel = () => {
    switch (selectedTab) {
      case 'userAppInstances':
        return 'New Application';
      case 'dataSourceInstances':
        return 'New Datasource';
      case 'agentInstances':
        return 'New AI Agent';
      default:
        return 'Create New';
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        height: { xs: 'auto', md: '100%' },
        p: { xs: 1.5, sm: 3 },
        pt: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        overflow: { xs: 'visible', md: 'hidden' },
      }}
    >
      {/* Header section with back button and active action button */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button
            onClick={() => navigate('/dashboard')}
            startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
            variant="outlined"
            size="small"
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.4)',
              border: 1,
              borderColor: 'divider',
              backdropFilter: 'blur(10px)',
              borderRadius: '10px',
              px: 1.5,
              py: 0.75,
              fontWeight: 600,
              fontSize: '0.8rem',
              color: 'text.primary',
              textTransform: 'none',
              whiteSpace: 'nowrap',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.6)',
                borderColor: theme.palette.secondary.main,
              },
              transition: 'all 0.2s ease',
            }}
          >
            Back to Dashboard
          </Button>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary' }}>
              Instance Management
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Deploy, monitor, and configure your active services.
            </Typography>
          </Box>
        </Stack>

        <Button
          variant="contained"
          color="secondary"
          onClick={() => navigate(getAddRoute())}
          startIcon={<AddIcon />}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            borderRadius: '12px',
            px: 3,
            py: 1,
            fontWeight: 700,
            color: 'text.primary',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
              bgcolor: alpha(theme.palette.secondary.main, 0.9),
            },
            transition: 'all 0.2s ease',
          }}
        >
          {getAddLabel()}
        </Button>
      </Stack>

      {/* Main card containing Tabs and Content */}
      <Box
        sx={{
          flexGrow: 1,
          minHeight: { xs: 450, md: 0 },
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'rgba(255, 255, 255, 0.3)',
          backdropFilter: 'blur(10px)',
          borderRadius: '24px',
          border: 1,
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        {/* Navigation Tabs bar */}
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 2.5,
            bgcolor: alpha(theme.palette.background.paper, 0.5),
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            indicatorColor="secondary"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: 52,
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                minWidth: 120,
                minHeight: 52,
                fontWeight: 600,
                fontSize: '0.9rem',
                color: 'text.secondary',
                gap: 1,
                '&.Mui-selected': {
                  color: 'primary.main',
                },
              },
            }}
          >
            <Tab
              value="userAppInstances"
              label="Applications"
              icon={<AppsIcon sx={{ fontSize: 16 }} />}
              iconPosition="start"
            />
            <Tab
              value="dataSourceInstances"
              label="Data Sources"
              icon={<StorageIcon sx={{ fontSize: 16 }} />}
              iconPosition="start"
            />
            <Tab
              value="agentInstances"
              label="AI Agents"
              icon={<SmartToyIcon sx={{ fontSize: 16 }} />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Dynamic Content Panel area */}
        <Box sx={{ flexGrow: 1, position: 'relative', overflow: 'hidden', p: 2 }}>
          <TabPanel
            value="userAppInstances"
            currentTab={selectedTab}
            maxHeight={containerSize.height > 0 ? (isMobile ? 500 : containerSize.height - 150) : 500}
          >
            <MyUserAppInstances maxHeight={containerSize.height > 0 ? (isMobile ? 450 : containerSize.height - 180) : 450} />
          </TabPanel>
          <TabPanel
            value="dataSourceInstances"
            currentTab={selectedTab}
            maxHeight={containerSize.height > 0 ? (isMobile ? 500 : containerSize.height - 150) : 500}
          >
            <MyDataSourceInstances maxHeight={containerSize.height > 0 ? (isMobile ? 450 : containerSize.height - 180) : 450} />
          </TabPanel>
          <TabPanel
            value="agentInstances"
            currentTab={selectedTab}
            maxHeight={containerSize.height > 0 ? (isMobile ? 500 : containerSize.height - 150) : 500}
          >
            <MyAgentInstances maxHeight={containerSize.height > 0 ? (isMobile ? 450 : containerSize.height - 180) : 450} />
          </TabPanel>
        </Box>
      </Box>
    </Box>
  );
};

export default InstancesPage;
